/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from "@msdyn365-commerce-modules/utilities";
import * as React from "react";
import _ from 'lodash';
import {
  ICheckoutViewProps,
  ILineItem,
  ILineItemDeliveryGroup,
  ILineItems,
  IOrderSummary,
  IPickUpAtStore
} from "@msdyn365-commerce-modules/checkout/src/modules/checkout/./checkout";
import { IEmailDelivery } from "@msdyn365-commerce-modules/checkout/src/modules/checkout/./components";
import {
  IInvoicePaymentSummary
} from "@msdyn365-commerce-modules/checkout/src/modules/checkout/./components/get-invoice-payment-summary";
import {
  IChargeLinesBreakdownData,
  ICheckoutProps as ICheckoutExtensionProps
} from "../definition-extensions/checkout.ext.props.autogenerated";
import { AttributeTextValue, Cart } from "@msdyn365-commerce/retail-proxy";
import { LinePagination } from "./components/line-pagination";


export interface IOrderSummaryExtended extends IOrderSummary {
  otherChargesLines: React.ReactNode[];
}

export interface ILineItemsExtended extends ILineItems {
  numItems: number;
  prev: string;
  next: string;
}

export interface ILineItemDeliveryGroupExtended extends ILineItemDeliveryGroup {
  numItems: number;
  prev: string;
  next: string;
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
  <Node {...PickUpAtStore}>
    {label}
    {location}
  </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) =>
  <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
  <Node {...LineItem}>
    {item}
    {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
    {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
  </Node>
);
//
export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroupExtended> = ({
                                                                                   LineItemDeliveryGroup,
                                                                                   LineItemList,
                                                                                   heading,
                                                                                   lineItems,
                                                                                   numItems,
                                                                                   prev,
                                                                                   next
                                                                                 }) => {
  if (lineItems && lineItems.length > 0 && !lineItems[0].item) {
    return null;
  }

  const defaultPageLength: number = 5;
  const pageLength = numItems || defaultPageLength;
  const [paginatedView, setPaginatedView] = React.useState<ILineItem[]>((lineItems && lineItems.length >= pageLength) ? lineItems.slice(0, pageLength) : lineItems);
  const [paginationPage, setPaginationPage] = React.useState<number>(0);

  const nextPage = () => {
    changePage(paginationPage + 1);
  };

  const prevPage = () => {
    changePage(paginationPage - 1);
  };

  const changePage = (page: number) => {
    if (page < 0) {
      return;
    }

    if (lineItems && (page > lineItems.length / pageLength)) {
      return;
    }

    let skip = (page * pageLength);
    if (lineItems && lineItems.length >= skip) {
      setPaginationPage(page);
      let slice = lineItems?.slice(skip);
      setPaginatedView(slice.length >= pageLength ? slice.slice(0, pageLength) : slice);
    }
  };

  const pagination = () => {
    return (
      <LinePagination
        nextPage={nextPage}
        prevPage={prevPage}
        changePage={changePage}
        currentPage={paginationPage}
        numPages={lineItems.length ? Math.ceil(lineItems.length / (pageLength)) : 0}
        prev={prev}
        next={next}
      />
    );
  };

  if (paginatedView.length === 0 || !paginatedView[0].item) {
    return null;
  }
  return (
    <Node {...LineItemDeliveryGroup}>
      {heading}
      <Node {...LineItemList}>
        {/* {lineItems.map(lineItem => ( */}
        {paginatedView.map(lineItem => {
          return <LineItemComponent key={lineItem.LineId} {...lineItem} />;
        })}
      </Node>
      {pagination()}
    </Node>
  );
};

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
                                                                                             LineItemDeliveryGroup,
                                                                                             LineItemList,
                                                                                             heading,
                                                                                             lineItems,
                                                                                             lineItemWraper,
                                                                                             lineItemWraperIcon
                                                                                           }) => (
  <Node {...LineItemDeliveryGroup}>
    {lineItemWraperIcon}
    {lineItemWraper}
    {heading}
    <Node {...LineItemList}>
      {lineItems.map(lineItem => (
        <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
      ))}
    </Node>
  </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({
                                                                           LineItem,
                                                                           item,
                                                                           pickUpAtStore,
                                                                           emailDelivery
                                                                         }) => (
  <Node {...LineItem}>
    {item}
    {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
  </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({
                                                                                     PickUpAtStore,
                                                                                     label,
                                                                                     location
                                                                                   }) => (
  <Node {...PickUpAtStore}>
    {label}
    {location}
  </Node>
);

export const LineItemsComponent: React.FC<ILineItemsExtended> = ({
                                                                   LineItems,
                                                                   Header,
                                                                   heading,
                                                                   editLink,
                                                                   itemsForPickup,
                                                                   itemsForShip,
                                                                   itemsForEmail,
                                                                   itemsGroupWithMulitplePickupMode,
                                                                   numItems,
                                                                   prev,
                                                                   next
                                                                 }) => {
  const items = itemsForPickup ? itemsForPickup : itemsForEmail ? itemsForEmail : itemsForShip;
  if (!items) {
    return null;
  }
  const lineItemGroupComponentExt: ILineItemDeliveryGroupExtended = { ...items!, numItems, prev, next };

  return (
    <Node {...LineItems}>
      <Node {...Header}>
        {heading}
        {editLink}
      </Node>
      {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup &&
        <LineItemGroupComponent {...lineItemGroupComponentExt} />}
      {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail &&
        <LineItemGroupComponent {...lineItemGroupComponentExt} />}
      {itemsGroupWithMulitplePickupMode === undefined && itemsForShip &&
        <LineItemGroupComponent {...lineItemGroupComponentExt} />}
      {itemsGroupWithMulitplePickupMode !== undefined
        ? itemsGroupWithMulitplePickupMode.map((item, index) => {
          return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
        })
        : null}
    </Node>
  );
};


const OrderSummaryComponent: React.FC<IOrderSummaryExtended> = ({ heading, lines, otherChargesLines }) => {
  const shippingLine = lines?.shipping;
  if (shippingLine) { 
    _.set((shippingLine as object), 'props.freeText', 'To Be Calculated')
  }
  console.log(shippingLine);
  return (
  <div className="msc-order-summary-wrapper">
    {heading}
    <div className="msc-order-summary__items">
      {lines && (
        <>
          {lines.subtotal}
          {shippingLine}
          {lines.otherCharge}
          {otherChargesLines && <p className="msc-order-summary__line-other-charges__summary">{otherChargesLines}</p>}
          {lines.tax}
          {/* {lines.totalDiscounts} */}
          {lines.loyalty}
          {lines.giftCard}
          {lines.orderTotal}
        </>
      )}
    </div>
  </div>
)};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
  <div className="msc-invoice-summary-wrapper">
    {heading}
    <div className="msc-invoice-summary__items">
      {lines && (
        <>
          {lines.invoices}
          {lines.giftCard}
          {lines.loyalty}
          {lines.orderTotal}
        </>
      )}
    </div>
  </div>
);

const _renderShipOnCheckout = (
  shipCompleteOnCheckout: boolean,
  handleShipCompleteOnCheckoutChange: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void,
  shipCompleteOnCheckoutLabel: string
): React.ReactNode => (
  <div className="ms-checkout__ship-on-checkout">
    <label className="input-group-prepend print-view-toggle__label">
      <span className={"print-view-toggle__heading"}>{shipCompleteOnCheckoutLabel}</span>
      <input
        value={shipCompleteOnCheckout ? 1 : 0}
        onClick={handleShipCompleteOnCheckoutChange}
        type="checkbox"
        className="print-view-toggle__checkbox"
      />
      <div
        className={`print-view-toggle__toggle print-view-toggle__toggle--${shipCompleteOnCheckout ? "checked" : "unchecked"}`} />
    </label>
  </div>
);

const _renderOrderComment = (commentHeading: string, commentString: string): React.ReactNode => {
  const emptyStringClassName = commentString == "";
  return (
    <div
      className={`order-level-comment-wrapper ${emptyStringClassName ? "order-level-comment-wrapper--empty-string" : ""}`}>
      <h1 className="order-level-comment-wrapper__heading">
        {commentHeading}
      </h1>
      <p className="order-level-comment-wrapper__text">
        {commentString}
      </p>
    </div>
  );
};


const otherChargesLines = (cart: Cart, chargeCodes: IChargeLinesBreakdownData[] | undefined, currencyFormatter: (price: string | number, currencyCode?: string | undefined) => string): React.ReactNode[] => {

  let lines = cart.ChargeLines;
  let linesNode: React.ReactNode[] = [];
  chargeCodes?.map(c => {
    const node = lines?.filter(
      line => line.ChargeCode === c.chargeCode).map(
      r => <p className="msc-order-charge-line"><span
        className="msc-order-charge-line__label">{c.chargeLineLabel}</span> <span
        className="msc-order-charge-line__value">{currencyFormatter(r.CalculatedAmount?.toString() ?? "")}</span></p>
    );
    linesNode.push(node);
  });
  return linesNode;
};

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutExtensionProps<{}>> = props => {
  const attributeArr = props.data.checkout.result?.checkoutCart.cart.AttributeValues || [];
  const getShipCompleteIndex = (): number | undefined => {
    return props.data.checkout.result?.checkoutCart.cart.AttributeValues?.findIndex(attribute => attribute.Name === "ShipComplete");
  };
  // Get the text in the order level comment, if it doesn't exist, assign an empty string
  const getCommentText = (): string => {
    return props.data.checkout.result?.checkoutCart.cart.Comment || "";
  };
  // Get the comment heading text from the resources
  const getCommentHeadingText = (): string => {
    return props.resources.commentHeadingText;
  };
  const getShipCompleteValue = (): boolean => {
    const shipCompleteIndex = getShipCompleteIndex();
    if (shipCompleteIndex === -1 || shipCompleteIndex === undefined) {
      return false;
    } else {
      const attr = attributeArr[shipCompleteIndex] as AttributeTextValue;
      return attr?.TextValue === 'true';
    }
  };
  const handleShipCompleteOnCheckoutChange = () => {
    const shipComplete = {
      "@odata.type": "#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue",
      Name: "ShipComplete",
      // TextValue: (!getShipCompleteValue()).toString()
      TextValue: (!shipCompleteOnCheckout).toString()
    };
    const idx = getShipCompleteIndex();
    idx === undefined || idx === -1 ? attributeArr.push(shipComplete) : attributeArr.splice(idx, 1, shipComplete);
    const cart: Cart = Object.assign({}, props.data.checkout.result?.checkoutCart.cart);
    if (cart) {
      cart.AttributeValues = attributeArr;
      saveShipCompleteToCart(cart);
    }
  };
  const saveShipCompleteToCart = async (cart: Cart) => {
    if (cart) {
      const checkoutState = props.data.checkout.result;
      if (!checkoutState) {
        return;
      }

      await checkoutState.checkoutCart.updateCart({ newCartObject: cart }).then(() => {
        // setShipCompleteOnCheckout(getShipCompleteValue());
        setShipCompleteOnCheckout(!shipCompleteOnCheckout);
      });
    }
  };
  const [shipCompleteOnCheckout, setShipCompleteOnCheckout] = React.useState<boolean>(getShipCompleteValue());
  const shipCompleteOnCheckoutToggle = _renderShipOnCheckout(
    shipCompleteOnCheckout,
    handleShipCompleteOnCheckoutChange,
    props.resources.shipCompleteOnCheckoutLabel
  );

  // const getProductEligibility = async (addressId: number): Promise<any> => {
  //     // get items ids after removing duplicates
  //     const checkoutState = props.data.checkout.result;
  //     let productToRemove: any[] = [];
  //     let list: EligibleItems = [];
  //
  //     if (!checkoutState) {
  //         return;
  //     }
  //
  //     const itemIds = [...new Set(cart.CartLines?.map(p => p.ItemId!) || [])];
  //
  //     if (itemIds.length) {
  //
  //         await getProductEligibilityAsync({ callerContext: props.context.actionContext }, itemIds, addressId, checkoutState.checkoutCart.cart.Id || '')
  //             .then((result: any) => {
  //                 result.forEach((i: any) => {
  //                     const id = getExtensionPropertyValueByKey(i.ExtensionProperties, "ItemId", CommercePropertyValueEnum.string);
  //                     list.push({
  //                         ItemId: String(id),
  //                         IsEligible: i.IsEligible
  //                     });
  //                 });
  //             });
  //
  //         cart.CartLines?.forEach(c => {
  //             list.forEach(i => {
  //                 if (i.ItemId === c.ItemId && !i.IsEligible) {
  //                     productToRemove.push(
  //                         {
  //                             LineId: c.LineId || '',
  //                             ItemId: c.ItemId
  //                         }
  //                     );
  //                 }
  //             });
  //         });
  //         if (productToRemove.length) {
  //             await checkoutState.checkoutCart.removeCartLines({ cartLineIds: productToRemove.map(p => p.LineId) });
  //         }
  //         return productToRemove;
  //     }
  // }

  const _renderAccountLockedMessage = (): React.ReactNode => (
    <div className="account-lock-alert">{props.config.accountLockedMessage}</div>
  );

  const {
    canShow,
    checkoutProps,
    headerProps,
    hasSalesOrder,
    hasInvoiceLine,
    bodyProps,
    mainProps,
    mainControlProps,
    sideProps,
    sideControlFirstProps,
    sideControlSecondProps,
    termsAndConditionsProps,
    orderConfirmation,
    loading,
    alert,
    title,
    guidedForm,
    orderSummary,
    invoicePaymentSummary,
    lineItems,
    placeOrderButton,
    termsAndConditions,
    keepShoppingButton
  } = props;
  const defaultPageLength: number = 5;
  const pageLength = props.config.pageLength || defaultPageLength;
  const cart: Cart = Object.assign({}, props.data.checkout.result?.checkoutCart.cart);
  let chargelines = otherChargesLines(cart, props.config.chargeLinesBreakdown, props.context.cultureFormatter.formatCurrency);
  const orderSummaryExtended: IOrderSummaryExtended = { ...orderSummary!, otherChargesLines: chargelines };
  // const [productsRemoved, setProductsRemoved] = React.useState<any>(null);

  // if (MsDyn365.isBrowser && cart.CartLines) {
  //     const shippingId = (window as any).ShippingId;
  //     const recId = cart.ShippingAddress?.RecordId;
  //
  //     // if (recId && shippingId !== recId) {
  //     //     (window as any).ShippingId = recId;
  //     //     getProductEligibility(recId).then(result => {
  //     //         if (result.length) {
  //     //             setProductsRemoved(result);
  //     //             document.dispatchEvent(new CustomEvent("UPDATE_CART", { detail: { Products: result } }));
  //     //         }
  //     //     });
  //     // }
  // }

  const isAccountBlocked: boolean | undefined = props.data.customerInformation.result?.Blocked;
  const lineItemsExt: ILineItemsExtended = {
    ...lineItems!,
    numItems: pageLength,
    prev: props.resources.paginationPrev || "",
    next: props.resources.paginationNext || ""
  };
  
  return (
    <Module {...checkoutProps}>
      {/*{productsRemoved && productsRemoved.length ? generateProductsRemovedMessage(productsRemoved, productEligibilityText) : null}*/}
      {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
      {!hasSalesOrder && (
        <Node {...bodyProps}>
          {loading}
          {isAccountBlocked ? _renderAccountLockedMessage() : null}
          {alert}
          {canShow && (
            <>
              <Node {...mainProps}>
                {guidedForm}
                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                {props.config.shipCompleteOnCheckout && shipCompleteOnCheckoutToggle}
                <Node {...mainControlProps}>
                  {placeOrderButton}
                  {keepShoppingButton}
                </Node>
              </Node>
              <Node {...sideProps}>
                {!hasInvoiceLine
                  ? orderSummary && <OrderSummaryComponent {...orderSummaryExtended} />
                  : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                <Node {...sideControlFirstProps}>
                  <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                  {placeOrderButton}
                  {keepShoppingButton}
                </Node>
                {lineItems && <LineItemsComponent {...lineItemsExt} />}
                {_renderOrderComment(getCommentHeadingText(), getCommentText())}
                <Node {...sideControlSecondProps}>
                  <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                  {placeOrderButton}
                  {keepShoppingButton}
                </Node>
              </Node>
            </>
          )}
        </Node>
      )}
      {hasSalesOrder && orderConfirmation}
    </Module>
  );
};

export default CheckoutView;
